// All Firebase access goes through here. The SDK is imported lazily so it stays out
// of the initial bundle (~150 KB gzipped) and only loads once someone starts a form.

const loadSdk = () =>
  Promise.all([import('../firebase'), import('firebase/firestore'), import('firebase/storage')]);

let preload;
export function preloadBackend() {
  if (!preload) preload = loadSdk().catch(() => (preload = undefined));
  return preload;
}

async function sdk() {
  const [app, firestore, storage] = await loadSdk();
  if (!app.isConfigured) {
    throw new Error('Groni is not connected to its database yet. Please try again later.');
  }
  return { ...app, firestore, storageSdk: storage };
}

/**
 * Adds someone to the waitlist.
 *
 * The entry lives at `waitlist/{email}` (private) and is written in the same batch as
 * `waitlist-index/{email}`, which holds no personal data and is publicly readable.
 * firestore.rules only allow creating both together, so a repeat sign-up is rejected;
 * we then confirm the index document really exists before reporting `exists`, so any
 * other rejection (bad payload, rules not deployed) surfaces as an error instead.
 *
 * @param {{ firstName: string, lastName: string, email: string }} entry  email must be normalized
 * @returns {Promise<'joined' | 'exists'>}
 */
export async function joinWaitlist({ firstName, lastName, email }) {
  const { db, firestore } = await sdk();
  const { doc, getDoc, serverTimestamp, writeBatch } = firestore;
  const entryRef = doc(db, 'waitlist', email);
  const indexRef = doc(db, 'waitlist-index', email);

  const batch = writeBatch(db);
  batch.set(entryRef, {
    email,
    firstName,
    lastName,
    consentGiven: true,
    consentTimestamp: serverTimestamp(),
    createdAt: serverTimestamp(),
    source: 'web',
  });
  batch.set(indexRef, { createdAt: serverTimestamp() });

  try {
    await batch.commit();
    return 'joined';
  } catch (error) {
    if (error?.code !== 'permission-denied') throw error;
    const existing = await getDoc(indexRef).catch(() => null);
    if (existing?.exists()) return 'exists';
    throw error;
  }
}

const EXTENSIONS = { 'image/jpeg': '.jpg', 'image/png': '.png', 'image/gif': '.gif', 'image/webp': '.webp' };

/**
 * Step 1 of a submission: upload the prepared photo.
 * The path uses the future post id, never the submitter's name, and carries no custom
 * metadata (Storage objects are publicly readable, so names must not travel with them).
 * Returned details are reused if saving the post fails, so retries don't re-upload.
 */
export async function uploadPhoto({ image, onProgress }) {
  const { db, storage, firestore, storageSdk } = await sdk();
  const { collection, doc } = firestore;
  const { ref, uploadBytesResumable, getDownloadURL } = storageSdk;

  const postId = doc(collection(db, 'user-posts')).id;
  const fileName = `${Date.now()}_${postId}${EXTENSIONS[image.type] || '.jpg'}`;
  const storagePath = `uploads/${fileName}`;

  const task = uploadBytesResumable(ref(storage, storagePath), image.blob, { contentType: image.type });
  await new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      reject,
      resolve
    );
  });

  const url = await getDownloadURL(task.snapshot.ref);
  return { postId, fileName, storagePath, url, fileType: image.type, fileSize: image.size };
}

/** Step 2 of a submission: record the post that points at an uploaded photo. */
export async function savePost({ upload, fields, originalFileName }) {
  const { db, firestore } = await sdk();
  const { doc, setDoc, serverTimestamp } = firestore;

  await setDoc(doc(db, 'user-posts', upload.postId), {
    url: upload.url,
    storagePath: upload.storagePath,
    fileName: upload.fileName,
    firstName: fields.firstName,
    lastName: fields.lastName,
    location: fields.location,
    venue: fields.venue,
    rating: fields.rating,
    tags: fields.tags,
    instagram: fields.instagram,
    createdAt: serverTimestamp(),
    originalFileName: (originalFileName || 'photo').slice(0, 200),
    fileType: upload.fileType,
    fileSize: upload.fileSize,
  });
  return { id: upload.postId, url: upload.url };
}

/** Maps Firebase error codes to something a person can act on. */
export function friendlyError(error) {
  const code = error?.code || '';
  if (code.includes('unavailable') || code.includes('network') || code === 'storage/retry-limit-exceeded') {
    return 'You seem to be offline. Check your connection and try again.';
  }
  if (code === 'storage/unauthorized' || code === 'permission-denied') {
    return 'That submission was rejected. Please check the photo is under 5 MB and try again.';
  }
  if (code === 'storage/canceled') return 'Upload cancelled.';
  return error?.message && !code ? error.message : 'Something went wrong. Please try again.';
}
